import { useAuth } from "@clerk/clerk-react";
import { Box, Divider, Grid, Typography } from "@mui/material";
import { ActionItemsContext } from "contexts/ActionItems";
import { AlertContext } from "contexts/Alert";
import { DiscoverViewContext } from "contexts/DiscoverView";
import { NewCampaignProvider } from "contexts/NewCampaign";
import { OnboardingDialogsContext } from "contexts/OnboardingDialogs";
import { OrganizationUserContext } from "contexts/Organization";
import { OutreachTemplatesContext } from "contexts/OutreachTemplates";
import { SubscriptionContext } from "contexts/Subscription";
import { useContext, useEffect, useState } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import {
  ActionItemType,
  BrandDealJourney,
  SearchParams,
  UpgradeDialogSource,
  VaOrganizationProfileStatus,
} from "schemas/dashboard";
import {
  OrganizationAutopilotProfileStatus,
  OrganizationProProfileStatus,
} from "schemas/dashboard";
import { routes } from "schemas/routes";

import { PAGE_VISITED } from "constants/trackingProps";
import AutopilotOnboardingProgress from "features/Auth/Onboarding/AutopilotOnboarding/Progress";
import ProOnboardingProgress from "features/Auth/Onboarding/ProOnboarding/Progress";
import { fetcherAuth } from "utils/api";
import {
  DISPLAYED_INSTAGRAM_ONBOARDING_PAYWALL,
  DISPLAYED_ONBOARDING_PAYWALL,
  INSTAGRAM_FOLLOWED,
  INSTAGRAM_JOINED_CHANNEL,
} from "utils/localStorage";
import { getParams, trackEvent } from "utils/tracking";

import NewCampaign from "../Campaigns/NewCampaign";
import VaOnboardingProgress from "../VA/OnboardingProgress";
import ActionItemList from "./ActionItemList";
import DefaultActivityCard from "./ActionItemList/ActivityCard";
import CurrentPlanCard from "./CurrentPlanCard";
import Gamification from "./Gamification";
import HomeVideo from "./HomeVideo";
import OnboardingStepper from "./OnboardingStepper";
import PartnerOffersCarousel from "./PartnerOffers/Carousel";
import ResourcesHub from "./ResourcesHub";
import SpecialAnnouncementCard from "./SpecialAnnouncement";
import TipsFromCreators from "./TipsFromCreators";
import VirtualAssistantCard from "./VirtualAssistantCard";
import styles from "./styles";

const HomePage = () => {
  const { getToken } = useAuth();
  const { setErrorAlert } = useContext(AlertContext);
  const {
    currentOrg,
    organizationVaProfile,
    currentUser,
    organizationProProfile,
    organizationAutopilotProfile,
    totalEmailsSent,
    setTotalEmailsSent,
    showGamificationProfile,
    gamificationProfile,
    profile,
  } = useContext(OrganizationUserContext);
  const { discoverTab } = useContext(DiscoverViewContext);
  const { actionItems, setActionItems } = useContext(ActionItemsContext);
  const { outreachTemplates } = useContext(OutreachTemplatesContext);
  const { incompleteProfile, noTemplates } = useContext(
    OnboardingDialogsContext,
  );
  const { subscription, setUpgradeDialogSource } =
    useContext(SubscriptionContext);

  const [fetchLoading, setFetchLoading] = useState(false);

  const isFromInstagramOnboarding =
    localStorage.getItem(
      `${DISPLAYED_INSTAGRAM_ONBOARDING_PAYWALL}-${currentOrg?.id}`,
    ) === "true";
  const alreadyDisplayedOnboardingPaywall =
    localStorage.getItem(
      `${DISPLAYED_ONBOARDING_PAYWALL}-${currentOrg?.id}`,
    ) === "true";

  const upgradeDialogSource = getParams(SearchParams.UPGRADE_DIALOG_SOURCE);
  const showSteps =
    profile?.brandDealJourney === BrandDealJourney.work_regularly &&
    (outreachTemplates.length === 0 || totalEmailsSent === 0);

  const canDoCredit =
    (currentOrg?.isPaidUser || gamificationProfile?.unlockPremiumFeatures) &&
    !showSteps;

  const today = new Date();

  const getTodayDate = () => {
    return today.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const fetchActionItems = async () => {
    setFetchLoading(true);
    try {
      const res = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/action-items`,
      );
      setActionItems(res.actionItems);
    } catch (error) {
      setErrorAlert(error);
    } finally {
      setFetchLoading(false);
    }
  };

  const checkIfEmailsSent = async () => {
    setFetchLoading(true);
    try {
      const res = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/emails/total-count`,
      );
      setTotalEmailsSent(res.count);
    } catch (error) {
      setErrorAlert(error);
    } finally {
      setFetchLoading(false);
    }
  };

  const displayPaywallsPostOnboarding = () => {
    if (currentOrg?.isBrand) {
      return;
    }
    if (
      upgradeDialogSource &&
      Object.values(UpgradeDialogSource).includes(
        upgradeDialogSource as UpgradeDialogSource,
      )
    ) {
      setUpgradeDialogSource(upgradeDialogSource as UpgradeDialogSource);
      return;
    }
    const hasSubscribed = subscription?.isSubscribed;
    // Display usual onboarding paywall
    if (hasSubscribed) {
      return;
    }

    if (!alreadyDisplayedOnboardingPaywall) {
      setUpgradeDialogSource(UpgradeDialogSource.onboarding_paywall);
    }
  };

  useEffect(() => {
    if (currentOrg?.id) {
      fetchActionItems();
      checkIfEmailsSent();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOrg?.id]);

  useEffect(() => {
    if (currentOrg?.id) {
      displayPaywallsPostOnboarding();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentOrg?.id,
    alreadyDisplayedOnboardingPaywall,
    isFromInstagramOnboarding,
  ]);

  useEffect(() => {
    trackEvent(PAGE_VISITED);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (currentOrg?.isBrand) {
    return <Navigate to={`/${routes.inbox}`} replace />;
  }

  const renderOnboardingCard = () => {
    if (
      currentOrg?.isProUser &&
      organizationProProfile?.status === OrganizationProProfileStatus.later
    ) {
      return {
        title: <>{currentUser?.name}, complete your Pro Onboarding 👀</>,
        subtitle: "",
        body: <ProOnboardingProgress />,
      };
    } else if (
      currentOrg?.isAutopilotUser &&
      organizationAutopilotProfile?.status ===
        OrganizationAutopilotProfileStatus.later
    ) {
      return {
        title: <>{currentUser?.name}, complete your Autopilot Onboarding 👀</>,
        subtitle: "",
        body: <AutopilotOnboardingProgress />,
      };
    } else if (
      currentOrg?.isVaUser &&
      organizationVaProfile?.status &&
      ![
        VaOrganizationProfileStatus.completed,
        VaOrganizationProfileStatus.later,
      ]?.includes(organizationVaProfile?.status!)
    ) {
      return {
        title: (
          <>
            {currentUser?.name}, complete your Virtual Assistant Onboarding 👀
          </>
        ),
        subtitle: "",
        body: <VaOnboardingProgress />,
      };
    }
    return null;
  };

  const renderGamificationCard = () => {
    if (!showGamificationProfile) return null;
    return {
      title: canDoCredit ? (
        <>{currentUser?.name}, unlock free credits on Bento 💰</>
      ) : (
        <>
          {currentUser?.name},{" "}
          {showSteps
            ? "complete these steps below"
            : "unlock all features on Bento"}
        </>
      ),
      subtitle: canDoCredit ? (
        <>
          Turn today's actions into{" "}
          <Box component="span" sx={{ color: "#00BA13" }}>
            credits
          </Box>{" "}
          that you can use on your Bento subscription
        </>
      ) : (
        <>
          Complete these action items to{" "}
          <Box component="span" sx={{ color: "#00BA13" }}>
            {showSteps ? "unlock the next challenges" : "unlock all features"}
          </Box>{" "}
          on Bento
        </>
      ),
      body: <Gamification />,
    };
  };

  const hasClickedFollowed =
    localStorage.getItem(`${INSTAGRAM_FOLLOWED}-${currentOrg?.id}`) ===
      "true" ||
    localStorage.getItem(`${INSTAGRAM_JOINED_CHANNEL}-${currentOrg?.id}`) ===
      "true";

  const allStepperStepsComplete =
    !incompleteProfile &&
    hasClickedFollowed &&
    !noTemplates &&
    totalEmailsSent > 0;

  const renderStepperCard = () => {
    if (
      actionItems.length > 0 ||
      showGamificationProfile ||
      !!renderOnboardingCard()
    ) {
      return null;
    }

    return {
      title: (
        <>
          {currentUser?.name}, let's help you land your first brand deal of this
          month{" "}
        </>
      ),
      subtitle: (
        <>
          Complete these steps below to help Bento{" "}
          <Box component="span" sx={{ color: "#00BA13" }}>
            recommend the right brands
          </Box>{" "}
          for you.
        </>
      ),
      body: allStepperStepsComplete ? (
        <DefaultActivityCard />
      ) : (
        <OnboardingStepper totalEmailsSent={totalEmailsSent} />
      ),
    };
  };

  const renderActionItemsCard = () => {
    if (actionItems.length === 0) return null;
    return {
      title: (
        <>
          {currentUser?.name}, action items for you to do on {getTodayDate()}
        </>
      ),
      subtitle: (
        <>
          Complete these action items to{" "}
          <Box component="span" sx={{ color: "#00BA13" }}>
            increase your chances
          </Box>{" "}
          of landing brand deals.
        </>
      ),
      body: <ActionItemList fetchLoading={fetchLoading} />,
    };
  };

  const onboardingCard = renderOnboardingCard();
  const gamificationCard = renderGamificationCard();
  const stepperCard = renderStepperCard();
  const actionItemsCard = renderActionItemsCard();

  const highPriorityActionItem = actionItems.some(
    (item) =>
      item.type === ActionItemType.rate_discussion_follow_up ||
      item.type === ActionItemType.brand_response,
  );
  const orderedCards = highPriorityActionItem
    ? [onboardingCard, actionItemsCard, gamificationCard, stepperCard]
    : [onboardingCard, gamificationCard, stepperCard, actionItemsCard];

  return (
    <Grid container sx={{ pb: 10 }} gap={1} alignItems={"flex-start"}>
      <Grid item xs={12} lg={discoverTab !== null ? 12 : 8}>
        <SpecialAnnouncementCard />
        {orderedCards
          .filter((card) => card !== null)
          .map((card, index) => (
            <Box key={index} sx={[styles.whiteCard, { p: 3, mb: 1 }]}>
              <Typography
                variant="h2"
                sx={{
                  fontSize: { xs: 30, md: 40 },
                  lineHeight: 1.3,
                  mb: 2,
                }}
              >
                {card!.title}
              </Typography>

              <Typography variant="subtitle1" sx={{ mb: 2 }}>
                {card!.subtitle}
              </Typography>

              {card!.body}

              <Divider sx={{ my: 3, borderColor: "purple.light" }} />
            </Box>
          ))}

        <Box sx={styles.whiteCard}>
          <TipsFromCreators />
        </Box>
      </Grid>

      <Grid container item xs lg gap={1}>
        <Grid item xs={12}>
          <CurrentPlanCard />
        </Grid>
        <Grid item xs={12}>
          <HomeVideo />
        </Grid>
        <Grid item xs={12}>
          {!currentOrg?.isVaUser && !currentOrg?.isAutopilotUser && (
            <VirtualAssistantCard />
          )}
        </Grid>
        <Grid item xs={12}>
          <PartnerOffersCarousel />
        </Grid>
        <ResourcesHub />
      </Grid>

      <Routes>
        <Route
          path="new/*"
          element={
            <NewCampaignProvider>
              <NewCampaign />
            </NewCampaignProvider>
          }
        />
        <Route
          path="campaign/:campaignId/*"
          element={
            <NewCampaignProvider>
              <NewCampaign />
            </NewCampaignProvider>
          }
        />
      </Routes>
    </Grid>
  );
};

export default HomePage;
